import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getLatestTrades } from "@/lib/api";
import { toast } from "sonner";
import { Radio } from "lucide-react";

const KIND = { buy: { label: "BUY", cls: "text-green-400" }, sell: { label: "SELL", cls: "text-red-400" }, swap: { label: "SWAP", cls: "text-[#D4AF37]" } };
const num = (n, d) => Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const keyOf = (t) => `${t.txHash}-${t.logIndex}`;
const describe = (t) =>
  t.direction === "FMI→USDT" ? `${num(t.fmi, 4)} FMI → ${num(t.usdt, 2)} USDT` : `${num(t.usdt, 2)} USDT → ${num(t.fmi, 4)} FMI`;

const TickerItem = ({ t, dup }) => (
  <Link to={`/tx/${t.txHash}`} data-testid={dup ? undefined : "trade-ticker-item"} aria-hidden={dup || undefined} tabIndex={dup ? -1 : undefined}
    className="inline-flex items-center gap-2 px-4 text-xs whitespace-nowrap hover:text-white transition-colors">
    <span className={`font-bold ${KIND[t.kind].cls}`}>{KIND[t.kind].label}</span>
    <span className="font-mono text-zinc-300">{describe(t)}</span>
    <span className="font-mono text-zinc-500">@ {num(t.price, 4)}</span>
    <span className="text-[#2A2411]">◆</span>
  </Link>
);

const useNewTradeToasts = (trades) => {
  const seen = useRef(null);
  useEffect(() => {
    if (!trades) return;
    if (seen.current) {
      trades.filter((t) => !seen.current.has(keyOf(t))).slice(0, 3).forEach((t) =>
        toast(`New ${KIND[t.kind].label.toLowerCase()}: ${describe(t)}`, { description: `@ ${num(t.price, 4)} USDT per FMI` }));
    }
    seen.current = new Set(trades.map(keyOf));
  }, [trades]);
};

export const TradeTicker = () => {
  const { data } = useQuery({ queryKey: ["latestTrades"], queryFn: () => getLatestTrades(15), refetchInterval: 4000 });
  const trades = data?.trades;
  useNewTradeToasts(trades);
  if (!trades?.length) return null;
  const duration = Math.max(trades.length * 4, 20);

  return (
    <div data-testid="trade-ticker" className="flex items-center rounded-full border border-[#2A2411] bg-[#0A0A0A]/80 overflow-hidden">
      <span className="flex items-center gap-1.5 shrink-0 px-4 py-2 text-xs font-semibold text-[#D4AF37] border-r border-[#2A2411] bg-[#050505]">
        <Radio size={13} className="animate-pulse" /> <span className="hidden sm:inline">Live trades</span>
      </span>
      <div className="ticker-mask relative flex-1 overflow-hidden py-2">
        <div className="ticker-track inline-flex" style={{ animationDuration: `${duration}s` }}>
          {trades.map((t) => <TickerItem key={keyOf(t)} t={t} />)}
          {trades.map((t) => <TickerItem key={`dup-${keyOf(t)}`} t={t} dup />)}
        </div>
      </div>
    </div>
  );
};
